// @module:knowledge-base
/**
 * Knowledge Base constants — shared between frontend and backend.
 */

export const KB_CONSTANTS = {
  /** Maximum file size in bytes (50 MB) — keep in sync with nginx's client_max_body_size */
  MAX_FILE_SIZE: 50 * 1024 * 1024,
  /** Maximum number of files per single upload batch */
  MAX_FILES_PER_UPLOAD: 10,
  /** Allowed MIME types for document upload */
  ALLOWED_MIME_TYPES: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/plain',
    'text/markdown',
  ] as const,
  /** Allowed file extensions */
  ALLOWED_EXTENSIONS: ['.pdf', '.docx', '.txt', '.md'] as const,
  // CONVERSION_TIMEOUT_MINUTES / MAX_RETRY_COUNT / SWEEP_INTERVAL_MS are gone (BACKLOG 5.17):
  // they parameterised the BFF conversion sweeper, which bounded a job's own retries while
  // creating unbounded NEW jobs (446 862 rows for 2 027 documents, 438 768 of them for one
  // document in 31 hours). Conversion is owned by the ingestion worker, and the bound now lives on
  // the document: kb_objects.conversion_attempts, migration 048, KB_MAX_CONVERSION_ATTEMPTS in the
  // worker's own configuration.
  //
  // MAX_CONVERSION_RETRIES below is NOT a revival of MAX_RETRY_COUNT. That one bounded an
  // automatic sweeper's retries (and bounded nothing, since job creation was unbounded). This one
  // bounds how many times a HUMAN may spend a fresh conversion budget by clicking Retry. Nothing
  // automatic reads it, so it cannot create a job, let alone a storm.
  //
  // WHY IT EXISTS (measured 2026-09-07, deployed worker logs): the retry endpoint resets
  // conversion_attempts to 0, so one document failed three times, ~42 minutes each, and every run
  // logged `attempt 1 of 5`. KB_MAX_CONVERSION_ATTEMPTS could therefore NEVER fire for a document
  // whose failure mode is "too slow to convert", and each click silently burned another 42 minutes
  // of docling CPU. See migration 068 (kb_objects.conversion_retries).
  //
  // 3 is a product judgement, not a measurement: enough that a genuine transient (a cold pod, a
  // gateway 504, a rebalance) never blocks a user, small enough that a document which cannot
  // convert costs ~2 hours of CPU rather than an unbounded amount.
  /** How many times a user may grant a document a fresh conversion budget via Retry. */
  MAX_CONVERSION_RETRIES: 3,
  /** Frontend polling interval in milliseconds (5 seconds) */
  POLL_INTERVAL_MS: 5 * 1000,
} as const;

export type AllowedMimeType = (typeof KB_CONSTANTS.ALLOWED_MIME_TYPES)[number];

// ─── L3 Entity Candidate Type Taxonomy ───────────────────────────────────────

/**
 * Closed taxonomy of entity candidate types used by the `entity_candidates` L3
 * extraction. The LLM must classify each entity into EXACTLY ONE of these 16
 * types. The taxonomy is universal — no domain-specific ontology required.
 *
 * Groups:
 *   Physical world  — Material, Organism, Location, Device
 *   Abstract        — Concept, Method, Model, Standard, Dataset
 *   Measurable      — Metric, Property
 *   Agents / social — Person, Organization, Event
 *   Document        — Reference, Unknown
 */
export const ENTITY_CANDIDATE_TYPES = [
  // Physical world
  'Material',       // substances, chemicals, composites, drugs, polymers
  'Organism',       // biological entities, cell lines, model organisms
  'Location',       // geographic places, labs, facilities, regions
  'Device',         // instruments, sensors, machines, equipment
  // Abstract / intellectual
  'Concept',        // theories, phenomena, effects, abstract ideas
  'Method',         // protocols, algorithms, processes, techniques
  'Model',          // ML models, mathematical models, simulations
  'Standard',       // ISO norms, IEEE specs, regulatory frameworks
  'Dataset',        // databases, corpora, benchmarks
  // Measurable
  'Metric',         // a specific measurement with value + unit
  'Property',       // a qualitative property without a specific value
  // Agents / social
  'Person',         // authors, inventors, researchers
  'Organization',   // companies, universities, labs, standards bodies
  'Event',          // conferences, experiments, trials, incidents
  // Document-structural
  'Reference',      // cited papers, standards, URLs, patents
  'Unknown',        // LLM uncertain — flag for human review
] as const;

export type EntityCandidateType = (typeof ENTITY_CANDIDATE_TYPES)[number];

/**
 * Group labels and colors for UI rendering.
 * Maps each group name to its entity types and a Carbon color token.
 */
export const ENTITY_TYPE_GROUPS: Record<string, { types: EntityCandidateType[]; color: string }> = {
  Physical:   { types: ['Material', 'Organism', 'Location', 'Device'],              color: 'teal' },
  Abstract:   { types: ['Concept', 'Method', 'Model', 'Standard', 'Dataset'],       color: 'blue' },
  Measurable: { types: ['Metric', 'Property'],                                       color: 'green' },
  Agents:     { types: ['Person', 'Organization', 'Event'],                          color: 'purple' },
  Document:   { types: ['Reference', 'Unknown'],                                     color: 'warm-gray' },
};

/**
 * Minimum content length (chars) for L3 text extractions (key_claims, entity_candidates).
 * Units shorter than this are headings/references — not worth extracting from.
 */
export const L3_TEXT_EXTRACTION_MIN_CHARS = 200;

// Made with Bob

